import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { LIFT_TO_1RM, PLYO_IDS, AEROBIC_IDS, ANAEROBIC_IDS } from "../lib/rescale";
import CustomExerciseModal from "../components/CustomExerciseModal";
import { useAuth } from "../contexts/AuthContext";

const DOMAINS = ["All", "Strength", "Endurance", "Mobility", "ACFT"];
const LEVELS = ["All", "Beginner", "Intermediate", "Advanced"];

// Single-tap presets. type: "equipment" (exact match on e.equipment),
// "muscle" (substring match against any muscle_group entry), or
// "set" (membership test against a curated id set).
const QUICK_CHIPS = [
  { id: "no-equipment", label: "No Equipment", type: "equipment", value: "None" },
  { id: "barbell", label: "Barbell", type: "equipment", value: "Barbell" },
  { id: "dumbbell", label: "Dumbbell", type: "equipment", value: "Dumbbell" },
  { id: "kettlebell", label: "Kettlebell", type: "equipment", value: "Kettlebell" },
  { id: "core", label: "Core Focus", type: "muscle", value: "Core" },
  { id: "cardio", label: "Cardio", type: "muscle", value: "Cardio" },
  { id: "power", label: "Power", type: "muscle", value: "Power" },
  { id: "posterior-chain", label: "Posterior Chain", type: "muscle", value: "Posterior" },
  { id: "plyo", label: "Plyometric", type: "set", value: PLYO_IDS },
  { id: "aerobic", label: "Aerobic", type: "set", value: AEROBIC_IDS },
  { id: "anaerobic", label: "Anaerobic", type: "set", value: ANAEROBIC_IDS },
  { id: "1rm", label: "1RM Auto-fill", type: "set", value: new Set(Object.keys(LIFT_TO_1RM)) },
  { id: "custom", label: "Custom Only", type: "custom" },
];

export default function ExerciseLibrary() {
  const { user } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [domain, setDomain] = useState("All");
  const [level, setLevel] = useState("All");
  const [q, setQ] = useState("");
  const [chip, setChip] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const loadExercises = () => {
    api.get("/exercises").then((r) => setExercises(r.data));
  };
  useEffect(() => { loadExercises(); }, []);

  const filtered = useMemo(() => {
    return exercises.filter((e) => {
      if (domain !== "All" && e.domain !== domain) return false;
      if (level !== "All" && e.level !== level) return false;
      if (q && !e.name.toLowerCase().includes(q.toLowerCase())) return false;
      if (chip) {
        if (chip.type === "equipment" && e.equipment !== chip.value) return false;
        if (chip.type === "muscle") {
          const hit = (e.muscle_groups || []).some((m) =>
            m.toLowerCase().includes(chip.value.toLowerCase())
          );
          if (!hit) return false;
        }
        if (chip.type === "set" && !chip.value.has(e.id)) return false;
        if (chip.type === "custom" && !e.custom) return false;
      }
      return true;
    });
  }, [exercises, domain, level, q, chip]);

  const toggleChip = (c) => setChip((curr) => (curr?.id === c.id ? null : c));
  const clearAll = () => { setDomain("All"); setLevel("All"); setQ(""); setChip(null); };

  const totalCount = exercises.length;
  const activeFilters = (domain !== "All" ? 1 : 0) + (level !== "All" ? 1 : 0) + (q ? 1 : 0) + (chip ? 1 : 0);

  return (
    <Layout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono mb-1">// FIELD MANUAL // SECTION 4</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black">EXERCISE LIBRARY</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Skill-tiered drills with image references, written instructions, and equipment-based alternates.
          </p>
        </div>
        {user && (
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-custom-exercise-btn"
            className="bg-volt text-black px-4 py-2 font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:opacity-90"
          >
            <Plus size={14} weight="bold" /> Add Custom Drill
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 mb-4">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search drills..."
                 className="w-full bg-secondary border border-border pl-10 pr-4 py-3 focus:outline-none focus:border-volt"
                 data-testid="library-search-input" />
        </div>
        <Pills options={DOMAINS} value={domain} onChange={setDomain} testidPrefix="filter-domain" />
        <Pills options={LEVELS} value={level} onChange={setLevel} testidPrefix="filter-level" />
      </div>

      {/* Quick filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="quick-filter-chips">
        <span className="label-mono text-muted-foreground mr-1 hidden sm:inline">// QUICK FILTERS</span>
        {QUICK_CHIPS.map((c) => {
          const active = chip?.id === c.id;
          return (
            <button
              key={c.id}
              onClick={() => toggleChip(c)}
              data-testid={`quick-chip-${c.id}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-display border transition-colors ${
                active
                  ? "bg-volt text-black border-volt"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
              }`}
            >
              {c.label}
            </button>
          );
        })}
        {activeFilters > 0 && (
          <button
            onClick={clearAll}
            data-testid="quick-chip-clear"
            className="ml-auto px-3 py-1.5 text-xs uppercase tracking-widest font-display border border-border text-muted-foreground hover:text-foreground hover:border-foreground"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="label-mono text-muted-foreground mb-4" data-testid="library-result-count">
        SHOWING {filtered.length} / {totalCount} DRILLS
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((e) => (
          <Link to={`/exercise/${e.id}`} key={e.id} className="tactical-card group block h-full relative" data-testid={`exercise-card-${e.id}`}>
            {e.custom && (
              <div className="absolute top-2 right-2 z-10 bg-volt text-black px-2 py-0.5 text-[10px] font-display uppercase tracking-widest font-black">
                {e.is_global ? "GLOBAL" : "CUSTOM"}
              </div>
            )}
            <div className="aspect-[4/3] overflow-hidden bg-secondary">
              <img
                src={e.image_url || "/icon-512.png"}
                alt={e.name}
                className={`w-full h-full ${e.image_url ? "object-cover" : "object-contain p-8 opacity-60"} group-hover:scale-105 transition-transform duration-500`}
                loading="lazy"
                onError={(ev) => { ev.currentTarget.src = "/icon-512.png"; ev.currentTarget.classList.add("object-contain","p-8","opacity-60"); ev.currentTarget.classList.remove("object-cover"); }}
              />
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="label-mono text-volt">{e.domain}</span>
                <span className="label-mono text-muted-foreground">· {e.level}</span>
              </div>
              <div className="font-display text-xl font-black uppercase leading-tight">{e.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{e.equipment} · {(e.muscle_groups || [e.body_part || "—"]).slice(0, 3).join(", ")}</div>
            </div>
          </Link>
        ))}
      </div>

      {filtered.length === 0 && <div className="label-mono mt-12 text-center">NO EXERCISES MATCH YOUR FILTER</div>}

      {showModal && (
        <CustomExerciseModal
          onClose={() => setShowModal(false)}
          onSaved={(newEx) => {
            setShowModal(false);
            loadExercises();
            // Optional: navigate to the new drill's detail
            // navigate(`/exercise/${newEx.id}`);
          }}
        />
      )}
    </Layout>
  );
}

function Pills({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1 border border-border p-1">
      {options.map((o) => (
        <button key={o} onClick={() => onChange(o)} data-testid={`${testidPrefix}-${o}`}
                className={`px-3 py-2 text-xs uppercase tracking-widest font-display ${value === o ? "bg-volt text-black" : "text-muted-foreground hover:text-foreground"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}
